import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {ApiErrorOptions, handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

/** What one save attempt has to do, decided from the drafts as they stand when it runs. */
export type DraftSaveRequest =
  /** Issues the requests and clears the drafts it persisted. Rejects with an `ApiError`. */
  | {type: 'SEND'; send: () => Promise<void>}
  /** Nothing is dirty, so the attempt succeeds without a request and without a notification. */
  | {type: 'NOTHING_TO_SEND'}
  /** The drafts are not savable. The caller has already reported why. */
  | {type: 'REJECTED'};

interface UseQueuedDraftSaveParams {
  /**
   * Decides what the attempt has to send. It is called when the attempt's turn comes rather than
   * when the save was requested, so it must read the drafts as they stand at that moment.
   */
  prepareRequest: () => DraftSaveRequest;
  onSavingChange?: (isSaving: boolean) => void;
  /** Extra handling for a failed request, on top of the shared error notification. */
  errorOptions?: ApiErrorOptions;
}

/**
 * Saves the pending drafts of one editor, queued behind a save that is still in flight, and owns
 * everything a save attempt does around the requests themselves: the saving signal the surrounding
 * page blocks navigation on, the success notification, and the error handling.
 *
 * The queued run asks for its request when its turn comes instead of when it was requested. A save
 * requested while another is pending therefore covers the drafts edited in the meantime, and a
 * repeated save of already persisted drafts finds nothing left to send. Returning the in-flight
 * promise instead would resolve `true` for drafts it never persisted, and the unsaved-changes
 * blocker would discard them.
 */
export function useQueuedDraftSave(params: UseQueuedDraftSaveParams) {
  const savePromiseRef = useRef<Promise<boolean> | null>(null);

  function runSave() {
    return match(params.prepareRequest())
      .with({type: 'REJECTED'}, () => Promise.resolve(false))
      .with({type: 'NOTHING_TO_SEND'}, () => Promise.resolve(true))
      .with({type: 'SEND'}, ({send}) => {
        params.onSavingChange?.(true);

        return send()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
            return true;
          })
          .catch((error: ApiError) => {
            handleApiError(error, params.errorOptions);
            return false;
          })
          .finally(() => {
            params.onSavingChange?.(false);
          });
      })
      .exhaustive();
  }

  return function requestSave() {
    const inFlightSave = savePromiseRef.current;
    const savePromise = isNotNil(inFlightSave) ? inFlightSave.then(() => runSave()) : runSave();

    savePromiseRef.current = savePromise;
    void savePromise.finally(() => {
      if (savePromiseRef.current === savePromise) {
        savePromiseRef.current = null;
      }
    });

    return savePromise;
  };
}
